import React from "react";

const GuideforAuthors = () => {
  return (
    <div>
      <h2 className="fw-bold font-size-23">Journal Policy - Publication Ethics</h2>

      <h3 className="fw-bold text-blue font-size-21">Introduction</h3>
      <p>
        At The International Journal of Interdisciplinary Studies and System
        Science (IJISSS), we are committed to upholding the highest standards of
        ethical conduct in academic publishing. We promote integrity,
        transparency, accountability, and scholarly excellence throughout our
        editorial and review processes. This policy outlines the ethical
        standards expected of all participants in the scholarly publishing
        process, including authors, reviewers, editors, and staff.
      </p>

      <p>
        Our practices align with internationally recognized guidelines,
        including those of the Committee on Publication Ethics (COPE), the
        International Committee of Medical Journal Editors (ICMJE), the World
        Association of Medical Journal Editors (WAME), and the Declaration of
        Helsinki for research involving human subjects.
      </p>

      <h3 className="fw-bold text-blue font-size-21">1. Ethical Guidelines and Standards</h3>
      <p>IJISSS adheres to the following core documents:</p>
      <ul>
        <li>
          Committee on Publication Ethics. (2011, March). Code of Conduct and
          Best Practice Guidelines for Journal Editors.
        </li>
        <li>
          Committee on Publication Ethics. (2018, January). Code of Conduct and
          Best Practice Guidelines for Journal Editors.
        </li>
        <li>
          Committee on Publication Ethics. (2019, November). Retraction
          Guidelines. https://doi.org/10.24318/cope.2019.1.4
        </li>
        <li>
          ICMJE. (2019). Recommendations for the Conduct, Reporting, Editing,
          and Publication of Scholarly Work in Medical Journals.
          http://www.icmje.org/icmje-recommendations.pdf
        </li>
        <li>
          National Health and Medical Research Council. (2018). National
          Statement on Ethical Conduct in Human Research 2007 (Updated 2018).
          Canberra: Australian Government.
        </li>
        <li>
          World Medical Association. (2013). Declaration of Helsinki: Ethical
          Principles for Medical Research Involving Human Subjects.
          https://www.wma.net/what-we-do/medical-ethics/declaration-of-helsinki/
        </li>
      </ul>

      <h3 className="fw-bold text-blue font-size-21">2. Research Integrity</h3>
      <p>
        IJISSS requires all research to be conducted and reported honestly,
        transparently, and with academic rigor. Authors must provide accurate
        accounts of their research, ensure original work, and properly
        acknowledge the work of others. Misrepresentation or fabrication of
        data, plagiarism, and unethical experimentation will result in rejection
        or retraction.
      </p>

      <p>
        Editorial teams are responsible for providing clear policies on research
        ethics relevant to their disciplines. Concerns regarding research
        integrity should be addressed to the editorial office or submitted via
        email to: publishingethics@----------. All concerns will be investigated
        per COPE procedures.
      </p>

      <h3 className="fw-bold text-blue font-size-21">3. Editorial Process and Responsibilities</h3>

      <h4 className="fw-bold text-blue font-size-19">3.1 Publication Decisions</h4>
      <p>
        Editors are fully responsible for deciding which manuscripts will be
        published. This decision is based on the manuscript's significance,
        originality, clarity, and alignment with the journal’s scope.
      </p>

      <h4 className="fw-bold text-blue font-size-19">3.2 Confidentiality</h4>
      <p>
        Manuscripts are handled with strict confidentiality. Editors and staff
        must not disclose any information about a manuscript to anyone other
        than the corresponding author, reviewers, and publisher.
      </p>

      <h4 className="fw-bold text-blue font-size-19">3.3 Conflicts of Interest</h4>
      <p>
        Editors must avoid any conflicts of interest with respect to manuscripts
        under consideration. If a conflict arises, another editor will be
        assigned.
      </p>

      <h4 className="fw-bold text-blue font-size-19">3.4 Peer Review</h4>
      <p>
        All submissions are subject to a double-blind peer review process.
        Authors and reviewers remain anonymous.
      </p>

      <h4 className="fw-bold text-blue font-size-19">3.5 Corrections and Retractions</h4>
      <p>
        If errors or ethical breaches are discovered, IJISSS will issue a
        correction or retraction, following COPE's Retraction Guidelines.
      </p>

      <h3 className="fw-bold text-blue font-size-21">4. Peer Review Process</h3>
      <p>Reviewers must:</p>
      <ul>
        <li>Provide objective, evidence-based assessments.</li>
        <li>Maintain confidentiality of all materials.</li>
        <li>Declare any conflicts of interest.</li>
        <li>Notify editors if unable to review in a timely manner.</li>
        <li>Identify relevant, uncited literature.</li>
      </ul>

      <h3 className="fw-bold text-blue font-size-21">5. Authorship and Contributorship</h3>
      <p>Authorship must reflect substantial contributions to:</p>
      <ul>
        <li>The conception/design of the study.</li>
        <li>Data acquisition, analysis, or interpretation.</li>
        <li>Drafting or revising the manuscript.</li>
        <li>Final approval and accountability for all aspects of the work.</li>
      </ul>

      <p>
        IJISSS encourages ORCID iDs and follows CRediT (Contributor Roles
        Taxonomy). Authorship disputes will follow COPE guidelines.
      </p>

      <h3 className="fw-bold text-blue font-size-21">6. Plagiarism and Misuse of Sources</h3>
      <p>
        All manuscripts are screened using iThenticate (CrossCheck). Plagiarism
        or misuse of sources results in rejection or retraction.
      </p>

      <h3 className="fw-bold text-blue font-size-21">7. Duplicate and Redundant Publication</h3>
      <p>
        IJISSS does not allow simultaneous submission to multiple journals or
        republishing without transparency.
      </p>

      <h3 className="fw-bold text-blue font-size-21">8. Conflicts of Interest and Funding Disclosure</h3>
      <p>Authors must disclose:</p>
      <ul>
        <li>Funding sources</li>
        <li>Employment affiliations</li>
        <li>Consultancies</li>
        <li>Stock ownership</li>
      </ul>

      <h3 className="fw-bold text-blue font-size-21">9. Human Participants and Ethics Approval</h3>
      <p>
        Research must follow the Declaration of Helsinki (2013). IRB approval,
        informed consent, and confidentiality must be ensured.
      </p>

      <h3 className="fw-bold text-blue font-size-21">10. Fraudulent Research and Data Manipulation</h3>
      <p>
        Fabrication, falsification, or manipulation of data is strictly
        prohibited.
      </p>

      <h3 className="fw-bold text-blue font-size-21">11. Transparency and Data Availability</h3>
      <p>Authors should:</p>
      <ul>
        <li>Retain and provide data upon request</li>
        <li>Include a Data Availability Statement</li>
        <li>Use appropriate repositories</li>
      </ul>

      <h3 className="fw-bold text-blue font-size-21">12. Copyright and Licensing</h3>
      <p>IJISSS publishes under CC BY 4.0. Authors retain copyright.</p>

      <h3 className="fw-bold text-blue font-size-21">13. Marketing and Social Media</h3>
      <p>All promotional activity must follow ethical standards (ASA, IPRA).</p>

      <h3 className="fw-bold text-blue font-size-21">14. Advertising Policy</h3>
      <p>
        Advertisements must not influence editorial decisions and must follow
        ethical guidelines.
      </p>

      <h3 className="fw-bold text-blue font-size-21">15. Media and Public Relations</h3>
      <p>Media communication must comply with the IPRA Code of Conduct.</p>

      <h3 className="fw-bold text-blue font-size-21">16. Metrics and Reporting</h3>
      <p>IJISSS complies with COUNTER and supports Crossref and DORA.</p>

      <h3 className="fw-bold text-blue font-size-21">17. Integrity of the Scholarly Record</h3>
      <p>
        IJISSS maintains accurate metadata and updates records in accordance
        with COPE.
      </p>

      <h3 className="fw-bold text-blue font-size-21">Conclusion</h3>
      <p>
        IJISSS strives to ensure ethical, transparent, and high-quality academic
        publishing. We value the trust of our community and remain committed to
        scholarly integrity.
      </p>
    </div>
  );
};

export default GuideforAuthors;
